import React, { useState, useEffect } from 'react';
import { Button, Input, List, message, Select, Spin, Dropdown, Tag } from 'antd';
import type { MenuProps } from 'antd';
import { DownloadOutlined, EditOutlined, MoreOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { fetchEmployeesList, fetchTimeZoneList, fetchDepartmentList, fetchManageUser, fetchEmployeeTask, fetchAdminTaskList } from '../../fetch/fetch';
import moment from 'moment';

interface Leader {
  leader_id: string;
  leader_name: string;
}

const EmployeesList: React.FC<{ jwtToken: string; selectedEmployeeUid?: string }> = ({ jwtToken, selectedEmployeeUid }) => {
  const [visible, setVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 480;
  });
  const [newMember, setNewMember] = useState({
    email: '',
    name: '',
    timezone: '',
    department: '',
    leadership: '',
    uid: "",
    comments: "",
    roleTitle: ""
  });
  const [employees, setEmployees] = useState<any[]>([]);
  const [TimeZoneList, setTimeZoneList] = useState<any[]>([]);
  const [DepartmentList, setDepartmentList] = useState<any[]>([]);
  const [leadersList, setLeadersList] = useState<Leader[]>([]);
  const [tasks, setTasks] = useState<any>([]);
  const [SelectTasks, setSelectTasks] = useState<any>([]);
  const navigate = useNavigate();

  // Loading state for the list of employees
  const [loading, setLoading] = useState(true);

  // Loading state for form submission (Add/Edit member)
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    getList();
    // Fetch timezones
    fetchTimeZoneList(jwtToken).then((res) => {
      if (res.status === "1") {
        setTimeZoneList(res.response);
      } else if (res.status === "-1") {
        navigate('/logout');
      } else {
        message.error(res.message);
      }
    }).catch((error: any) => {
      console.log(error);
    });

    // Fetch departments
    fetchDepartmentList(jwtToken).then((res) => {
      if (res.status === "1") {
        setDepartmentList(res.response);
      } else if (res.status === "-1") {
        navigate('/logout');
      } else {
        message.error(res.message);
      }
    }).catch((error: any) => {
      console.log(error);
    });
    adminTask()
  }, [jwtToken]);

  const adminTask = () => {
    fetchAdminTaskList(jwtToken).then((res) => {
      if (res.status === "1") {
        setTasks(res.response);
      } else if (res.status === "-1") {
        navigate('/logout');
      } else {
        setTasks([]);
        message.error(res.message);
      }
    }).catch((error: any) => {
      setTasks([]);
      console.log(error);
    });
  }

  const getList = () => {
    setLoading(true);
    // Fetch employees list
    fetchEmployeesList(jwtToken).then((res) => {
      setLoading(false);
      if (res.status === "1") {
        setEmployees(res.response);
      } else if (res.status === "-1") {
        navigate('/logout');
      } else {
        message.error(res.message);
      }
    }).catch((error: any) => {
      console.log(error);
      setLoading(false);
    });
  }

  const handleAddMember = async () => {
    if (!newMember.email || !newMember.name || !newMember.timezone || !newMember.department || !newMember.leadership) {
      message.error('Please fill in all the required fields!');
      return;
    }

    setSubmitLoading(true); // Set loading to true when the API call starts

    try {
      const params = {
        email: newMember.email,
        name: newMember.name,
        time_zone: newMember.timezone,
        leader_uid: newMember.leadership,
        department_id: newMember.department,
        role_title: newMember.roleTitle,
        ...(isEditing && { uid: newMember.uid }), // Only include uid if editing
        task_id:SelectTasks,
        comment:newMember.comments
      };

      const response = await fetchManageUser(jwtToken, params);

      if (response.status === '1') {
        message.success(isEditing ? 'Member updated successfully!' : 'Member added successfully!');
        setNewMember({ email: '', name: '', timezone: '', department: '', leadership: '', uid: '', comments: '', roleTitle: '' });
        setVisible(false);
        setIsEditing(false);
        getList();
        setSelectTasks([])
      } else if (response.status === "-1") {
        navigate('/logout');
      } else {
        message.error(response.message || 'An error occurred');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      message.error('Failed to add or update member');
    } finally {
      setSubmitLoading(false); // Reset loading state when the API call finishes
    }
  };

  const handleEditMember = (employee: any) => {
    const selectedDepartment = DepartmentList.find(
      (department) => department.department_id === employee.department_id
    );

    if (selectedDepartment) {
      setLeadersList(selectedDepartment.leaders);
    }
    setNewMember({
      email: employee.email,
      name: employee.name,
      timezone: employee.time_zone,
      department: employee.department_id,
      leadership: employee.leader_uid,
      uid: employee.uid,
      comments: employee.comments,
      roleTitle: (employee as any)?.role_title ?? ''
    });
    setIsEditing(true);
    setVisible(true);
  };

  useEffect(() => {
    if(newMember.uid){
      setSubmitLoading(true)
      fetchEmployeeTask(jwtToken, {uid:newMember.uid}).then((res) => {
        setSubmitLoading(false)
        if (res.status === "1") {
          const taskId = res.response.map((items:any) => {
            return items.task_id
          })
          setSelectTasks(taskId)
        } else if (res.status === "-1") {
          navigate('/logout');
        } else {
          message.error(res.message);
        }
      }).catch((error: any) => {
        setSubmitLoading(false)
        console.log(error);
      });
    }
  }, [newMember.uid])

  const handleDepartmentChange = (value: string) => {
    const selectedDepartment = DepartmentList.find(department => department.department_id === value);
    if (selectedDepartment) {
      // setLeadersList(selectedDepartment.leaders);
      setNewMember({ ...newMember, department: value });
    }
  };

  const handleTimeReportDownload = async (employeeUid: string) => {
    const end = moment().startOf('day').format('YYYY-MM-DD');
    const start = moment().startOf('day').subtract(2, 'weeks').format('YYYY-MM-DD');

    const param = {
      search_start_date: start,
      search_end_date: end,
      employee_id: employeeUid,
    };

    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${jwtToken}`);
      myHeaders.append("Content-Type", "application/json");
      const API_URL = 'https://l9nd2oj8q9.execute-api.us-east-2.amazonaws.com/beta/dkj';
      const url = `${API_URL}/Admin/download`;

      const response = await fetch(url, {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify(param),
        redirect: "follow",
      });

      if (!response.ok) {
        throw new Error("File download failed");
      }

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `Time-Report (${employeeUid}) ${start} - ${end}`;
      link.click();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error during file download:', error);
      message.error('Failed to download time report');
    }
  };

  const handleAllTimeReportDownload = async () => {
    const end = moment().startOf('day').format('YYYY-MM-DD');
    const start = moment().startOf('day').subtract(2, 'weeks').format('YYYY-MM-DD');

    const param = {
      search_start_date: start,
      search_end_date: end,
    };

    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${jwtToken}`);
      myHeaders.append("Content-Type", "application/json");
      const API_URL = 'https://l9nd2oj8q9.execute-api.us-east-2.amazonaws.com/beta/dkj';
      const url = `${API_URL}/Admin/download`;

      const response = await fetch(url, {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify(param),
        redirect: "follow",
      });

      if (!response.ok) {
        throw new Error("File download failed");
      }

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `Time-Report (All) ${start} - ${end}`;
      link.click();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error during file download:', error);
      message.error('Failed to download time report');
    }
  };

  const filteredEmployees = selectedEmployeeUid
    ? employees.filter((e: any) => String(e.uid ?? '') === String(selectedEmployeeUid))
    : employees;

  return (
    <div style={{ padding: '20px', maxWidth: '760px', margin: '0 auto' }}>
      {loading ? (
        <Spin tip="Loading..." style={{ display: 'block', margin: '100px auto' }} />
      ) : (
        <>
          <List
            itemLayout="horizontal"
            dataSource={filteredEmployees}
            renderItem={(employee) => (
              <div
                className="manageTaskListCard"
                style={{
                  marginBottom: 16,
                  width: '100%',
                  borderRadius: 24,
                  background: 'linear-gradient(180deg, #FFFFFC 0%, #FBFBF7 100%)',
                  boxShadow: '0 2px 20px 0 rgba(0, 0, 0, 0.06)',
                  padding: '18px 22px 20px',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                }}
                onClick={() => handleEditMember(employee)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: '1px solid #E1E1E1',
                      background: '#FBFBF7',
                      color: '#0E1F27',
                      fontSize: 13,
                      fontWeight: 600,
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {`ID ${employee.uid ?? ''}`.trim()}
                  </span>

                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'edit',
                          label: 'Edit Profile',
                          icon: <EditOutlined />,
                          onClick: ({ domEvent }: any) => {
                            domEvent?.stopPropagation?.();
                            handleEditMember(employee);
                          },
                        },
                        {
                          key: 'time-report',
                          label: 'Time report',
                          icon: <DownloadOutlined />,
                          onClick: ({ domEvent }: any) => {
                            domEvent?.stopPropagation?.();
                            handleTimeReportDownload(String(employee.uid ?? ''));
                          },
                        },
                      ] as MenuProps['items'],
                    }}
                    trigger={['click']}
                    placement="bottomRight"
                    getPopupContainer={() => document.body}
                    overlayStyle={{ zIndex: 2000 }}
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<MoreOutlined style={{ fontSize: 18, color: '#8A8A86' }} />}
                      style={{ padding: '4px 6px', minWidth: 32 }}
                      aria-label="Member actions"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Dropdown>
                </div>

                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 500,
                    color: '#171717',
                    lineHeight: 1.45,
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {employee.name && employee.email
                    ? `${employee.name} - ${employee.email}`
                    : employee.name || '—'}
                </div>
              </div>
            )}
          />
          <div style={{ height: 60 }}></div>

          <div className="manage-bottom-actions">
            <Button
              className="manage-bottom-btn manage-bottom-download"
              onClick={() => {
                if (filteredEmployees.length === 1) {
                  const uid = String((filteredEmployees[0] as any)?.uid ?? '');
                  if (uid) handleTimeReportDownload(uid);
                  else handleAllTimeReportDownload();
                } else {
                  handleAllTimeReportDownload();
                }
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <span>Time report</span>
                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M18.1936 7.12754C18.1936 6.89268 18.2632 6.66309 18.3937 6.46781C18.5242 6.27253 18.7097 6.12033 18.9266 6.03045C19.1436 5.94058 19.3824 5.91706 19.6127 5.96288C19.8431 6.0087 20.0547 6.12179 20.2207 6.28787C20.3868 6.45394 20.4999 6.66552 20.5457 6.89587C20.5915 7.12622 20.568 7.36498 20.4781 7.58196C20.3883 7.79895 20.2361 7.98441 20.0408 8.11489C19.8455 8.24537 19.6159 8.31501 19.3811 8.31501C19.0661 8.31501 18.7641 8.1899 18.5414 7.96721C18.3187 7.74451 18.1936 7.44247 18.1936 7.12754ZM17.0061 4.75259C17.241 4.75259 17.4706 4.68294 17.6659 4.55246C17.8611 4.42198 18.0133 4.23652 18.1032 4.01954C18.1931 3.80256 18.2166 3.5638 18.1708 3.33345C18.125 3.1031 18.0119 2.89151 17.8458 2.72544C17.6797 2.55937 17.4681 2.44627 17.2378 2.40046C17.0075 2.35464 16.7687 2.37815 16.5517 2.46803C16.3347 2.55791 16.1493 2.71011 16.0188 2.90539C15.8883 3.10067 15.8187 3.33025 15.8187 3.56511C15.8187 3.88005 15.9438 4.18209 16.1665 4.40479C16.3892 4.62748 16.6912 4.75259 17.0061 4.75259ZM19.8422 10.2941C19.633 10.2769 19.4256 10.3434 19.2655 10.479C19.1053 10.6147 19.0056 10.8084 18.9882 11.0175C18.8465 12.6725 18.2351 14.2526 17.2259 15.5719C16.2167 16.8912 14.8516 17.8948 13.2914 18.4646C11.7312 19.0345 10.0406 19.1468 8.41871 18.7884C6.79679 18.43 5.31097 17.6159 4.13607 16.4417C2.96117 15.2675 2.14607 13.7822 1.78668 12.1605C1.42729 10.5388 1.53855 8.84816 2.10738 7.28754C2.67621 5.72692 3.67895 4.36124 4.9976 3.35118C6.31625 2.34112 7.89596 1.7287 9.55085 1.58599C9.65481 1.57741 9.75606 1.54844 9.84883 1.50073C9.94159 1.45303 10.0241 1.38751 10.0915 1.30794C10.1589 1.22836 10.2101 1.13628 10.2419 1.03695C10.2738 0.937621 10.2858 0.832989 10.2772 0.729028C10.2686 0.625067 10.2396 0.523814 10.1919 0.431048C10.1442 0.338283 10.0787 0.255823 9.99914 0.188377C9.91956 0.12093 9.82748 0.069817 9.72815 0.0379568C9.62882 0.00609656 9.52419 -0.0058872 9.42023 0.00268958C7.4649 0.170881 5.59828 0.894031 4.03998 2.08706C2.48168 3.28009 1.29652 4.89337 0.623939 6.73709C-0.0486393 8.58081 -0.180658 10.5783 0.243415 12.4945C0.667488 14.4107 1.63001 16.1659 3.01774 17.5537C4.40547 18.9414 6.16068 19.9039 8.07687 20.328C9.99305 20.7521 11.9905 20.6201 13.8342 19.9475C15.6779 19.2749 17.2912 18.0897 18.4842 16.5314C19.6772 14.9731 20.4003 13.1064 20.5685 11.1511C20.5775 11.0471 20.5657 10.9423 20.534 10.8428C20.5023 10.7433 20.4512 10.6511 20.3837 10.5715C20.3162 10.4918 20.2336 10.4263 20.1406 10.3787C20.0477 10.3311 19.9463 10.3024 19.8422 10.2941ZM10.2772 3.16929C11.6863 3.16929 13.0638 3.58715 14.2355 4.37004C15.4072 5.15293 16.3204 6.26568 16.8596 7.56757C17.3989 8.86947 17.54 10.302 17.2651 11.6841C16.9901 13.0662 16.3116 14.3357 15.3152 15.3322C14.3187 16.3286 13.0492 17.0072 11.6672 17.2821C10.2851 17.557 8.85253 17.4159 7.55065 16.8766C6.24877 16.3374 5.13603 15.4242 4.35315 14.2525C3.57027 13.0808 3.15241 11.7033 3.15241 10.2941C3.15451 8.40515 3.90582 6.59414 5.24152 5.25842C6.57722 3.92271 8.38822 3.17138 10.2772 3.16929ZM9.48554 10.2941C9.48554 10.5041 9.56895 10.7055 9.71741 10.8539C9.86587 11.0024 10.0672 11.0858 10.2772 11.0858H15.027C15.237 11.0858 15.4383 11.0024 15.5868 10.8539C15.7353 10.7055 15.8187 10.5041 15.8187 10.2941C15.8187 10.0842 15.7353 9.88282 15.5868 9.73435C15.4383 9.58589 15.237 9.50249 15.027 9.50249H11.0688V5.54424C11.0688 5.33428 10.9854 5.13292 10.837 4.98446C10.6885 4.83599 10.4871 4.75259 10.2772 4.75259C10.0672 4.75259 9.86587 4.83599 9.71741 4.98446C9.56895 5.13292 9.48554 5.33428 9.48554 5.54424V10.2941ZM13.4437 2.37764C13.6786 2.37764 13.9082 2.30799 14.1035 2.17751C14.2987 2.04703 14.4509 1.86157 14.5408 1.64459C14.6307 1.42761 14.6542 1.18885 14.6084 0.958499C14.5626 0.728152 14.4495 0.516564 14.2834 0.350493C14.1173 0.184422 13.9058 0.0713256 13.6754 0.0255067C13.4451 -0.0203123 13.2063 0.00320364 12.9893 0.0930808C12.7723 0.182958 12.5869 0.335159 12.4564 0.530439C12.3259 0.725718 12.2563 0.955304 12.2563 1.19016C12.2563 1.5051 12.3814 1.80714 12.6041 2.02984C12.8268 2.25253 13.1288 2.37764 13.4437 2.37764Z"
                    fill="#0E1F27"
                  />
                </svg>
              </span>
            </Button>

            <Button
              className="manage-bottom-btn manage-bottom-new"
              type="primary"
              onClick={() => {
                setVisible(true);
                setNewMember({
                  email: '',
                  name: '',
                  timezone: '(GMT-06:00) America/Chicago',
                  department: '',
                  leadership: '',
                  uid: '',
                  comments: '',
                  roleTitle: '',
                });
              }}
            >
              <span className="manage-bottom-new-content">
                <span>New profile</span>
                <span className="manage-bottom-plus">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.75 0C7.82164 0 5.93657 0.571828 4.33319 1.64317C2.72982 2.71451 1.48013 4.23726 0.742179 6.01884C0.00422452 7.80042 -0.188858 9.76082 0.187348 11.6521C0.563554 13.5434 1.49215 15.2807 2.85571 16.6443C4.21928 18.0079 5.95656 18.9365 7.84787 19.3127C9.73919 19.6889 11.6996 19.4958 13.4812 18.7578C15.2627 18.0199 16.7855 16.7702 17.8568 15.1668C18.9282 13.5634 19.5 11.6784 19.5 9.75C19.4968 7.16513 18.4685 4.68705 16.6407 2.85927C14.813 1.03149 12.3349 0.00322505 9.75 0ZM13.5 10.5H10.5V13.5C10.5 13.6989 10.421 13.8897 10.2803 14.0303C10.1397 14.171 9.94892 14.25 9.75 14.25C9.55109 14.25 9.36033 14.171 9.21967 14.0303C9.07902 13.8897 9 13.6989 9 13.5V10.5H6C5.80109 10.5 5.61033 10.421 5.46967 10.2803C5.32902 10.1397 5.25 9.94891 5.25 9.75C5.25 9.55109 5.32902 9.36032 5.46967 9.21967C5.61033 9.07902 5.80109 9 6 9H9V6C9 5.80109 9.07902 5.61032 9.21967 5.46967C9.36033 5.32902 9.55109 5.25 9.75 5.25C9.94892 5.25 10.1397 5.32902 10.2803 5.46967C10.421 5.61032 10.5 5.80109 10.5 6V9H13.5C13.6989 9 13.8897 9.07902 14.0303 9.21967C14.171 9.36032 14.25 9.55109 14.25 9.75C14.25 9.94891 14.171 10.1397 14.0303 10.2803C13.8897 10.421 13.6989 10.5 13.5 10.5Z" fill="white"/>
                      </svg>
                </span>
              </span>
            </Button>
          </div>

          {visible && (
            <div
              onClick={() => {
                if (!submitLoading) {
                  setVisible(false);
                  setIsEditing(false);
                  setNewMember({ email: '', name: '', timezone: '', department: '', leadership: '', uid: '', comments: '', roleTitle: '' });
                  setSelectTasks([]);
                }
              }}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.35)',
                zIndex: 1400,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'stretch',
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100vw',
                  height: '94vh',
                  background: '#FBFBF7',
                  borderTopLeftRadius: isMobile ? 12 : 16,
                  borderTopRightRadius: isMobile ? 12 : 16,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}
              >
                <button
                  type="button"
                  aria-label="Close"
                  disabled={submitLoading}
                  onClick={() => {
                    if (!submitLoading) {
                      setVisible(false);
                      setIsEditing(false);
                      setNewMember({ email: '', name: '', timezone: '', department: '', leadership: '', uid: '', comments: '', roleTitle: '' });
                      setSelectTasks([]);
                    }
                  }}
                  style={{
                    position: 'absolute',
                    right: isMobile ? 14 : 20,
                    top: isMobile ? 14 : 20,
                    width: 36,
                    height: 36,
                    border: '1px solid #ECECE7',
                    borderRadius: '50%',
                    background: '#F5F5F2',
                    color: '#9D9D98',
                    fontSize: 20,
                    lineHeight: '34px',
                    textAlign: 'center',
                    padding: 0,
                    cursor: submitLoading ? 'not-allowed' : 'pointer',
                    zIndex: 5,
                  }}
                >
                  ×
                </button>
                <div
                  style={{
                    flex: 1,
                    padding: isMobile ? '14px 14px 12px' : '22px 28px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                  }}
                >
                  <div style={{ marginBottom: 8 }}>
                    <div
                      style={{
                        width: isMobile ? '100%' : 560,
                        margin: isMobile ? '0' : '0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? 26 : 32,
                          lineHeight: 1.15,
                          fontWeight: 600,
                          color: '#171717',
                          letterSpacing: '-0.02em',
                        }}
                      >
                        {isEditing ? 'Manage profile' : 'New profile'}
                      </div>
                    </div>
                  </div>

                  <div style={{ flex: 1, overflowY: 'auto' ,marginTop: 20}}>
                    <div
                      style={{
                        width: '100%',
                        maxWidth: 560,
                        margin: '0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ width: 170, fontSize: 13, color: '#475467', fontWeight: 500 }}>Work Email</div>
                        <Input
                          placeholder="email@example.com"
                          value={newMember.email}
                          onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                          disabled={isEditing}
                          style={{ flex: 1, borderRadius: 10, height: 40, borderColor: '#E1E1E1', background: '#fff' }}
                        />
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ width: 170, fontSize: 13, color: '#475467', fontWeight: 500 }}>Full Name</div>
                        <Input
                          placeholder="John Doe"
                          value={newMember.name}
                          onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                          style={{ flex: 1, borderRadius: 10, height: 40, borderColor: '#E1E1E1', background: '#fff' }}
                        />
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ width: 170, fontSize: 13, color: '#475467', fontWeight: 500 }}>Time Zone</div>
                        <Select
                          placeholder="Select time zone"
                          value={newMember.timezone}
                          onChange={(value) => { setNewMember({ ...newMember, timezone: value }); }}
                          style={{ flex: 1 }}
                          size="large"
                          showSearch
                          getPopupContainer={() => document.body}
                          dropdownStyle={{ zIndex: 2000 }}
                          filterOption={(input, option) =>
                            String(option?.children).toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {TimeZoneList.map((timezone, index) => (
                            <Select.Option key={index} value={timezone.name}>
                              {timezone.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ width: 170, fontSize: 13, color: '#475467', fontWeight: 500 }}>Office/Supervisor</div>
                        <div style={{ flex: 1, display: 'flex', gap: 10 }}>
                          <Select
                            placeholder="Select office"
                            value={newMember.department}
                            onChange={handleDepartmentChange}
                            style={{ flex: 1 }}
                            size="large"
                            getPopupContainer={() => document.body}
                            dropdownStyle={{ zIndex: 2000 }}
                          >
                            {DepartmentList.map((department, index) => (
                              <Select.Option key={index} value={department.department_id}>
                                {department.name}
                              </Select.Option>
                            ))}
                          </Select>
                          <Select
                            placeholder="Select supervisor"
                            value={newMember.leadership}
                            onChange={(value) => setNewMember({ ...newMember, leadership: value })}
                            style={{ flex: 1 }}
                            size="large"
                            getPopupContainer={() => document.body}
                            dropdownStyle={{ zIndex: 2000 }}
                          >
                            {employees.map((leader, index) => (
                              <Select.Option key={index} value={leader.uid}>
                                {leader.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ width: 170, fontSize: 13, color: '#475467', fontWeight: 500 }}>Role title</div>
                        <Input
                          placeholder="Enter role, e.g. Project Manager"
                          value={newMember.roleTitle}
                          onChange={(e) => setNewMember({ ...newMember, roleTitle: e.target.value })}
                          style={{ flex: 1, borderRadius: 10, height: 40, borderColor: '#E1E1E1', background: '#fff' }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: 14 }}>
                        <div style={{ width: 170, fontSize: 13, color: '#475467', fontWeight: 500, paddingTop: 10 }}>Comments</div>
                        <Input.TextArea
                          placeholder="Comments"
                          value={newMember.comments}
                          onChange={(e) => setNewMember({ ...newMember, comments: e.target.value })}
                          rows={4}
                          style={{ flex: 1, borderRadius: 10, borderColor: '#E1E1E1', background: '#fff', resize: 'none' }}
                        />
                      </div>

                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                        <div
                          style={{
                            width: 170,
                            fontSize: 13,
                            color: '#475467',
                            fontWeight: 500,
                            paddingTop: 10,
                            flexShrink: 0,
                          }}
                        >
                          Assigned projects
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Select
                            mode="multiple"
                            placeholder="Start typing or select project..."
                            value={SelectTasks}
                            onChange={(value) => { setSelectTasks(value); }}
                            style={{ width: '100%' }}
                            size="large"
                            maxTagCount={0}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              String((option as any)?.children ?? '')
                                .toLowerCase()
                                .includes(String(input ?? '').toLowerCase())
                            }
                            getPopupContainer={() => document.body}
                            dropdownStyle={{ zIndex: 2000 }}
                            dropdownRender={(menu) => <div>{menu}</div>}
                          >
                            {tasks.map((t: any, index: any) => (
                              <Select.Option key={index} value={t.task_id}>
                                {t.title}
                              </Select.Option>
                            ))}
                          </Select>
                          {Array.isArray(SelectTasks) && SelectTasks.length > 0 && (
                            <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {SelectTasks.map((taskId: any) => {
                                const t = tasks.find((x: any) => String(x.task_id) === String(taskId));
                                const display = t?.title ? String(t.title) : String(taskId);
                                return (
                                  <Tag
                                    key={String(taskId)}
                                    closable
                                    closeIcon={
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          width: 17,
                                          height: 17,
                                        }}
                                      >
                                        <svg
                                          width="17"
                                          height="17"
                                          viewBox="0 0 17 17"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                          style={{ display: 'block' }}
                                        >
                                          <path
                                            d="M11.0672 6.06719L9.0086 8.125L11.0672 10.1828C11.1253 10.2409 11.1713 10.3098 11.2027 10.3857C11.2342 10.4616 11.2504 10.5429 11.2504 10.625C11.2504 10.7071 11.2342 10.7884 11.2027 10.8643C11.1713 10.9402 11.1253 11.0091 11.0672 11.0672C11.0091 11.1253 10.9402 11.1713 10.8643 11.2027C10.7884 11.2342 10.7071 11.2503 10.625 11.2503C10.5429 11.2503 10.4616 11.2342 10.3857 11.2027C10.3098 11.1713 10.2409 11.1253 10.1828 11.0672L8.125 9.00859L6.06719 11.0672C6.00912 11.1253 5.94018 11.1713 5.86431 11.2027C5.78844 11.2342 5.70713 11.2503 5.625 11.2503C5.54288 11.2503 5.46156 11.2342 5.38569 11.2027C5.30982 11.1713 5.24088 11.1253 5.18282 11.0672C5.12475 11.0091 5.07868 10.9402 5.04726 10.8643C5.01583 10.7884 4.99966 10.7071 4.99966 10.625C4.99966 10.5429 5.01583 10.4616 5.04726 10.3857C5.07868 10.3098 5.12475 10.2409 5.18282 10.1828L7.24141 8.125L5.18282 6.06719C5.06554 5.94991 4.99966 5.79085 4.99966 5.625C4.99966 5.45915 5.06554 5.30009 5.18282 5.18281C5.30009 5.06554 5.45915 4.99965 5.625 4.99965C5.79086 4.99965 5.94992 5.06554 6.06719 5.18281L8.125 7.24141L10.1828 5.18281C10.2409 5.12474 10.3098 5.07868 10.3857 5.04725C10.4616 5.01583 10.5429 4.99965 10.625 4.99965C10.7071 4.99965 10.7884 5.01583 10.8643 5.04725C10.9402 5.07868 11.0091 5.12474 11.0672 5.18281C11.1253 5.24088 11.1713 5.30982 11.2027 5.38569C11.2342 5.46156 11.2504 5.54288 11.2504 5.625C11.2504 5.70712 11.2342 5.78844 11.2027 5.86431C11.1713 5.94018 11.1253 6.00912 11.0672 6.06719ZM16.25 8.125C16.25 9.73197 15.7735 11.3029 14.8807 12.639C13.9879 13.9752 12.719 15.0166 11.2343 15.6315C9.74966 16.2465 8.11599 16.4074 6.5399 16.0939C4.9638 15.7804 3.51606 15.0065 2.37976 13.8702C1.24346 12.7339 0.469628 11.2862 0.156123 9.71011C-0.157382 8.13401 0.00352044 6.50035 0.618482 5.0157C1.23344 3.53105 2.27485 2.2621 3.611 1.36931C4.94714 0.476523 6.51803 0 8.125 0C10.2792 0.00227486 12.3445 0.85903 13.8677 2.38227C15.391 3.90551 16.2477 5.97081 16.25 8.125ZM15 8.125C15 6.76525 14.5968 5.43604 13.8414 4.30545C13.0859 3.17487 12.0122 2.29368 10.756 1.77333C9.49971 1.25298 8.11738 1.11683 6.78376 1.3821C5.45014 1.64737 4.22513 2.30216 3.26364 3.26364C2.30216 4.22513 1.64738 5.45013 1.3821 6.78375C1.11683 8.11737 1.25298 9.49971 1.77333 10.7559C2.29368 12.0122 3.17487 13.0859 4.30546 13.8414C5.43605 14.5968 6.76526 15 8.125 15C9.94773 14.9979 11.6952 14.2729 12.9841 12.9841C14.2729 11.6952 14.9979 9.94773 15 8.125Z"
                                            fill="#0E1F27"
                                          />
                                        </svg>
                                      </span>
                                    }
                                    onClose={() => {
                                      setSelectTasks((prev: any) =>
                                        Array.isArray(prev) ? prev.filter((v: any) => String(v) !== String(taskId)) : prev
                                      );
                                    }}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 6,
                                      margin: 0,
                                      padding: '6px 12px',
                                      borderRadius: 10,
                                      border: '1px solid #E1E1E1',
                                      background: '#FBFBF7',
                                      color: '#344054',
                                      fontSize: 14,
                                      maxWidth: '100%',
                                      whiteSpace: 'normal',
                                      lineHeight: '20px',
                                    }}
                                  >
                                    {display}
                                  </Tag>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 14,
                      marginTop: 18,
                      paddingTop: 8,
                      paddingBottom: 8,
                      flexShrink: 0,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Button
                      disabled={submitLoading}
                      onClick={() => {
                        setVisible(false);
                        setIsEditing(false);
                        setNewMember({ email: '', name: '', timezone: '', department: '', leadership: '', uid: '', comments: '', roleTitle: '' });
                        setSelectTasks([]);
                      }}
                      style={{
                        height: 48,
                        paddingLeft: 28,
                        paddingRight: 28,
                        borderRadius: 999,
                        border: '1px solid #E4E7EC',
                        background: '#FFFFFF',
                        color: '#171717',
                        fontWeight: 500,
                        fontSize: 16,
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      onClick={handleAddMember}
                      loading={submitLoading}
                      size="large"
                      style={{
                        minWidth: isMobile ? 168 : 200,
                        height: 48,
                        paddingLeft: 24,
                        paddingRight: 18,
                        borderRadius: 999,
                        border: 'none',
                        background:
                          'radial-gradient(76.89% 119.16% at 50% -19.12%, #102D3D 0%, #0E1F27 100%), #0E1F27',
                        boxShadow: '0 9px 20px 0 rgba(14, 31, 39, 0.2)',
                      }}
                    >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <span>{isEditing ? 'Save Changes' : 'Save profile'}</span>
                      <span
                        style={{
                          width: 24,
                          height: 24,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M12 0C9.62663 0 7.30655 0.703788 5.33316 2.02236C3.35977 3.34094 1.8217 5.21508 0.913451 7.4078C0.00519941 9.60051 -0.232441 12.0133 0.230582 14.3411C0.693605 16.6689 1.83649 18.807 3.51472 20.4853C5.19295 22.1635 7.33115 23.3064 9.65892 23.7694C11.9867 24.2324 14.3995 23.9948 16.5922 23.0865C18.7849 22.1783 20.6591 20.6402 21.9776 18.6668C23.2962 16.6934 24 14.3734 24 12C23.9966 8.81843 22.7313 5.76814 20.4816 3.51843C18.2319 1.26872 15.1816 0.00335979 12 0ZM17.2685 9.88384L10.8069 16.3454C10.7212 16.4312 10.6194 16.4993 10.5073 16.5457C10.3953 16.5922 10.2752 16.6161 10.1538 16.6161C10.0325 16.6161 9.91243 16.5922 9.80037 16.5457C9.68831 16.4993 9.5865 16.4312 9.50077 16.3454L6.73154 13.5762C6.55834 13.4029 6.46103 13.168 6.46103 12.9231C6.46103 12.6781 6.55834 12.4432 6.73154 12.27C6.90475 12.0968 7.13967 11.9995 7.38462 11.9995C7.62957 11.9995 7.86449 12.0968 8.0377 12.27L10.1538 14.3873L15.9623 8.57769C16.0481 8.49193 16.1499 8.4239 16.2619 8.37748C16.374 8.33107 16.4941 8.30718 16.6154 8.30718C16.7367 8.30718 16.8568 8.33107 16.9688 8.37748C17.0809 8.4239 17.1827 8.49193 17.2685 8.57769C17.3542 8.66345 17.4223 8.76527 17.4687 8.87732C17.5151 8.98938 17.539 9.10948 17.539 9.23077C17.539 9.35206 17.5151 9.47216 17.4687 9.58421C17.4223 9.69627 17.3542 9.79808 17.2685 9.88384Z"
                            fill="white"
                          />
                        </svg>
                      </span>
                    </span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmployeesList;
